import { CalendarOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import moment, { Moment } from 'moment';
import { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  cartSelectors,
  offerFiltersSelectors,
  sessionSelectors,
} from 'store/state/selectors';
import {
  setBillingDate,
  setDeliveryDate,
  setPageProductList,
} from 'services/config';
import { offerFiltersActions, sessionActions } from 'store/state/slices';
import { useNavigate } from 'react-router-dom';
import DeliveryDate from '../DeliveryDate';
import * as S from './styles';

interface DeliveryDateSelectorProps {
  showLabel?: boolean;
}

function DeliveryDateSelector({ showLabel = true }: DeliveryDateSelectorProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const [billingDateValue, setBillingDateValue] = useState<Moment | null>(null);
  const [deliveryDateValue, setDeliveryDateValue] = useState<Moment | null>(
    null,
  );
  const [showDatePickerBilling, setShowDatePickerBilling] =
    useState<boolean>(false);
  const [showDatePickerDelivery, setShowDatePickerDelivery] =
    useState<boolean>(true);
  const totalVol = useSelector(cartSelectors.getVolTotal);
  const filter = useSelector(offerFiltersSelectors.getOfferFilters);

  const { deliveryDate, billingDate, producerUrl } = useSelector(
    sessionSelectors.getRoot,
  );

  const disableDatesDelivery = useCallback(
    (value: Moment) => value.isBefore(moment().subtract(1, 'days')),
    [],
  );

  const handleOnVisibleChange = useCallback(
    (event: boolean) => {
      setShowPopover(event);

      if (event === true) {
        if (deliveryDate) {
          setDeliveryDateValue(moment(deliveryDate));
        }
        if (billingDate) {
          setBillingDateValue(moment(billingDate));
        }
        setTimeout(() => {
          setShowDatePickerDelivery(event);
        }, 0);
      } else {
        setShowDatePickerDelivery(event);
      }
    },
    [billingDate, deliveryDate],
  );

  const handleOnOpenChangeDelivery = useCallback(
    (event: boolean) => {
      setShowDatePickerDelivery(event);

      if (deliveryDateValue !== null) {
        setShowDatePickerBilling(true);
      }
    },
    [deliveryDateValue],
  );

  const handleOnOpenChangeBilling = useCallback((event: boolean) => {
    setShowDatePickerBilling(event);
  }, []);

  const handleOnChangeDelivery = useCallback((value: Moment | null) => {
    setPageProductList(1);
    setBillingDateValue(value);
    setBillingDate(value?.format('YYYY-MM-DD') ?? '');
    setDeliveryDateValue(value);
    setDeliveryDate(value?.format('YYYY-MM-DD') ?? '');

    if (value) {
      setShowDatePickerBilling(true);
    }
  }, []);

  const disableDatesBilling = useCallback(
    (value: Moment) => {
      if (deliveryDateValue !== null) {
        const initalDay = moment(deliveryDateValue);
        const lastDay = moment(deliveryDateValue).add(1, 'days');

        return value.isAfter(lastDay) || value.isBefore(initalDay);
      }
      return false;
    },
    [deliveryDateValue],
  );

  const handleOnChangeBilling = useCallback((value: Moment | null) => {
    setPageProductList(1);
    setBillingDateValue(value);
    setBillingDate(value?.format('YYYY-MM-DD') ?? '');
  }, []);

  const filterProductsByDeliveryDate = useCallback(() => {
    const deliveryDates = {
      billingDate: billingDateValue?.format('YYYY-MM-DD') ?? '',
      deliveryDate: deliveryDateValue?.format('YYYY-MM-DD') ?? '',
    };
    dispatch(sessionActions.setDates(deliveryDates));
    handleOnVisibleChange(false);
    dispatch(
      offerFiltersActions.filter({
        ...filter,
        deliveryDate: deliveryDates.deliveryDate,
        businessDate: deliveryDates.billingDate,
      }),
    );
    navigate(
      `/${producerUrl}/products${
        filter.categories ? `/${filter.categories}` : ''
      }`,
    );
    setShowPopover(false);
  }, [
    dispatch,
    billingDateValue,
    deliveryDateValue,
    handleOnVisibleChange,
    filter,
    navigate,
    producerUrl,
  ]);

  const content = useMemo(
    () => (
      <DeliveryDate
        initialDatePicker={{
          disabled: totalVol > 0,
          disabledDate: disableDatesDelivery,
          value: deliveryDateValue,
          open: showDatePickerDelivery && showPopover,
          onOpenChange: handleOnOpenChangeDelivery,
          onChange: handleOnChangeDelivery,
        }}
        endDatePicker={{
          disabled: deliveryDateValue === null || totalVol > 0,
          disabledDate: disableDatesBilling,
          value: billingDateValue,
          open: showDatePickerBilling && showPopover && !showDatePickerDelivery,
          onOpenChange: handleOnOpenChangeBilling,
          onChange: handleOnChangeBilling,
        }}
        onConfirmation={filterProductsByDeliveryDate}
        producerUnavailableDates={[]}
      />
    ),
    [
      billingDateValue,
      deliveryDateValue,
      disableDatesBilling,
      disableDatesDelivery,
      filterProductsByDeliveryDate,
      handleOnChangeBilling,
      handleOnChangeDelivery,
      handleOnOpenChangeBilling,
      handleOnOpenChangeDelivery,
      showDatePickerBilling,
      showDatePickerDelivery,
      showPopover,
      totalVol,
    ],
  );

  return (
    <Popover
      visible={showPopover}
      placement="bottom"
      content={content}
      trigger="click"
      onVisibleChange={handleOnVisibleChange}
    >
      <S.DeliveryDateSelector>
        {showLabel && <S.Text small>Entrega (ENP)</S.Text>}
        <S.DateDisplay>
          <S.Text>
            {deliveryDate 
              ? moment(deliveryDate).format('DD/MM/YYYY') 
              : 'Selecionar data'
            }
          </S.Text>
          <CalendarOutlined />
        </S.DateDisplay>
      </S.DeliveryDateSelector>
    </Popover>
  );
}

export default memo(DeliveryDateSelector);
